import React, { useEffect, useState } from 'react';
import Fuse from 'fuse.js';
import { FiEye, FiEyeOff, FiPlus, FiTrash } from 'react-icons/fi';

import usersData from '../../fake_data/MOCK_DATA_User_List_2.json';
import { columnDefinitionUsers } from '../Table/component/Table/column';
import { TableUserList } from '../Table/component/Table';
import { RxUpdate } from "react-icons/rx";
import { useLocation, useNavigate } from 'react-router-dom';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import axios from 'axios';
import useAuth from '../../hooks/useAuth';
import CopyAccessIdButton from '../Dashboard/component/Dashboard/CopyButton'
import SearchBar from '../Dashboard/component/Dashboard/SearchBar'
import { IoCheckmark } from 'react-icons/io5';

const UsersList = () => {
  const column = columnDefinitionUsers;

  const { auth } = useAuth();
  const companyName = auth.companyName;
  const navigate = useNavigate();
  const location = useLocation();
  const axiosPrivate = useAxiosPrivate();

  const [data, setData] = useState([]);
  const [accessID, setAccessID] = useState("")
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'add' or 'edit'

  const [editableData, setEditableData] = useState({
    username: '',
    name: '',
    email: '',
    gender: '',
    userRole: '',
  });
  const [tempShowPassword, setTempShowPassword] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [rowToDelete, setRowToDelete] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      setIsLoading(true);
      try {

        const [res1, res2] = await Promise.all([
          axiosPrivate.get('/user/get-users', { signal: controller.signal }),
          axiosPrivate.get('/company/get-accessid', { signal: controller.signal })
        ]);

        if (isMounted) {
          setData(res1.data)
          setSearchResults(res1.data);
          setAccessID(res2.data.accessID)
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          // Log for debugging purposes
          // console.log('Request cancelled:', err.message);
        } else {
          console.error('Request failed:', err);
          // Only navigate if the error was not a cancellation
          // Check for a 403 status code specifically
          if (err.response && err.response.status === 403) {
            navigate('/sign-in', { state: { from: location }, replace: true });
          }
        }
      }
      setIsLoading(false);
    };

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
      // console.log('Cleanup: Cancelled any ongoing requests.');
    };
  }, [navigate, location, axiosPrivate]);

  // Filter data based on search term
  useEffect(() => {
    // Initialize Fuse with the list of users and the options for fuzzy search
    const fuse = new Fuse(data, {
      keys: ['username', 'name', 'email', 'userRole'],
      includeScore: true,
    });

    if (searchTerm.trim().length > 0) {
      const results = fuse.search(searchTerm).map((result) => result.item);
      setSearchResults(results);
    } else {
      setSearchResults(data); // Reset to original data if search term is cleared
    }
  }, [searchTerm, data]);

  const handleAddNewClick = () => {
    setEditableData({
      username: '',
      name: '',
      email: '',
      gender: '',
      userRole: '',
    });
    setModalMode('add');
    setShowContentModal(true);
  };

  const handleDeleteClick = () => {
    if (selectedRows.size > 0) {
      setShowDeleteModal(true);
    } else {
      alert('No rows selected'); // Or show a more user-friendly message
    }
  };

  const requestRowDeletion = (rowId) => {
    setRowToDelete(rowId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (rowToDelete !== null) {
      let isMounted = true;
      const controller = new AbortController();

      try {
        // Assuming you want to DELETE the user, not PATCH
        await axiosPrivate.delete(`/user/delete-user/${rowToDelete}`, {
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });

        if (isMounted) {
          // Update the state to reflect the deletion
          setData((prevData) =>
            prevData.filter((row) => row.id !== rowToDelete)
          );
          setRowToDelete(null);
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Deletion failed:', err);
          if (err.response) {
            // Redirect on 403 Forbidden or show an error message
            if (err.response.status === 403) {
              navigate('/sign-in', {
                state: { from: location },
                replace: true,
              });
            } else {
              alert(
                `Error: ${err.response.data.message || 'An error occurred.'}`
              );
            }
          }
        }
      } finally {
        if (isMounted) {
          setShowDeleteModal(false); // Close the modal upon completion or error
        }
      }

      // Cleanup function to prevent state updates if the component unmounts
      return () => {
        isMounted = false;
        controller.abort();
      };
    } else {
      // If rowToDelete is null, just close the modal
      setShowDeleteModal(false);
    }
  };

  const deleteSelectedRows = async () => {
    // Check if any rows are selected for deletion
    if (selectedRows.size > 0) {
      let isMounted = true;
      const controller = new AbortController();

      try {
        // Convert selectedRows Set to an array of user IDs
        const userIdsToDelete = Array.from(selectedRows);

        await axiosPrivate.delete('/user/delete-users', {
          data: { userIds: userIdsToDelete }, // Sending user IDs in the request body
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
        });

        if (isMounted) {
          // Filter out the deleted users from the current data
          setData((prevData) =>
            prevData.filter((row) => !selectedRows.has(row.id))
          );
          setSelectedRows(new Set()); // Clear selected rows after successful deletion
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error('Deletion failed:', err);
          if (err.response) {
            // Handle specific HTTP status codes here
            if (err.response.status === 403) {
              navigate('/sign-in', {
                state: { from: location },
                replace: true,
              });
            } else {
              alert(
                `Error: ${err.response.data.message || 'An error occurred.'}`
              );
            }
          }
        }
      } finally {
        if (isMounted) {
          setShowDeleteModal(false); // Close the modal upon completion or error
        }
      }

      // Cleanup function to prevent state updates if the component unmounts
      return () => {
        isMounted = false;
        controller.abort();
      };
    } else {
      // If no rows were selected, just close the modal
      setShowDeleteModal(false);
    }
  };

  // Function to handle edit requests
  const handleEditClick = (rowData) => {
    setEditableData(rowData);
    setModalMode('edit');
    setShowContentModal(true);
  };

  const handleRegenerateClick = async () => {
    setLoading(true);
    setSuccess(false);

    try {
      const response = await axiosPrivate.patch('/company/regenerate-accessid');
      setAccessID(response.data.newId)

      setSuccess(true);
      setTimeout(() => setSuccess(false), 2000); // Reset success icon after 2s
    }
    catch (error) {
      console.error('Failed to regenerate access ID:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleAddNewSubmit = async (event) => {
    event.preventDefault();

    // Basic validation
    if (
      editableData.username.trim().length < 3 ||
      editableData.name.trim().length < 3
    ) {
      alert('Username and Name must be at least 3 characters long');
      return;
    }

    if (
      modalMode === 'add' &&
      editableData.password !== editableData.confirmPassword
    ) {
      alert('Passwords do not match');
      return;
    }

    const endpoint =
      modalMode === 'edit'
        ? `/user/update-user/${editableData.id}`
        : '/company/invite-user';
    const method = modalMode === 'edit' ? 'patch' : 'post';

    // Prepare the data payload
    const payload =
      modalMode === 'edit'
        ? {
          username: editableData.username,
          fullName: editableData.name,
          gender: editableData.gender,
          userRole: editableData.userRole,
        }
        : {
          ...editableData,
          password: editableData.password,
          confirmPassword: editableData.confirmPassword,
        };

    try {
      const response = await axiosPrivate[method](endpoint, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      // Update the state based on the modal mode
      if (modalMode === 'edit') {
        setData((currentData) =>
          currentData.map((item) =>
            item.id === editableData.id ? { ...item, ...response.data } : item
          )
        );
      } else {
        // Assuming the response includes the newly added user
        setData((currentData) => [...currentData, response.data]);
      }

      // Reset form and close modal
      setEditableData({
        username: '',
        name: '',
        email: '',
        gender: '',
        userRole: '',
      });
      setShowContentModal(false);
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error('Submission failed:', err);
        const errorMsg = err.response?.data.message || 'An error occurred.';
        if (err.response?.status === 403) {
          navigate('/sign-in', { state: { from: location }, replace: true });
        } else {
          alert(`Error: ${errorMsg}`);
        }
      }
    }
  };

  if (isLoading) {
    return <div>Loading...</div>; // Or any loading spinner
  }

  return (
    <div className="flex flex-col p-4 md:p-10 w-full overflow-y-auto h-full">
      <div className="flex items-center md:flex-row justify-between items-center mb-4">
        <h2 className="flex items-center gap-x-2 flex-wrap text-xl md:text-2xl font-bold mb-4 md:mb-0">
          {companyName} <span className="text-base md:text-lg font-medium ">(Access ID: {accessID})</span> <CopyAccessIdButton accessId={accessID} />
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={handleRegenerateClick}
            disabled={loading}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors duration-200
        ${success ? 'bg-green-500 text-white' : 'bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-700 dark:bg-orange-600 dark:hover:bg-orange-800'}
        disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {success ? <IoCheckmark className="w-5 h-5" /> : <RxUpdate className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />}
            {!loading && !success && <span className="font-medium">Generate New ID</span>}
            {loading && <span className="font-medium">Generating...</span>}
            {success && <span className="font-medium">New ID Generated</span>}
          </button>
          {/*  className="flex items-center justify-center bg-orange-500 text-white py-2 px-4 rounded hover:bg-orange-700 dark:bg-orange-600 dark:hover:bg-orange-800 transition duration-300 w-30"
            aria-label="Regenerate access ID"
          >
            <RxUpdate className="mr-2" />
            <span className="flex-1 text-center">Regenerate ID</span>
          </button> */}
          <button
            onClick={handleAddNewClick}
            className="flex items-center justify-center bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-800 transition duration-300 w-40"
            aria-label="Add new item"
          >
            <FiPlus className="mr-2" />
            <span className="flex-1 text-center">Add</span>
          </button>
          <button
            onClick={handleDeleteClick}
            className="flex items-center justify-center bg-red-500 text-white py-2 px-4 rounded hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-800 transition duration-300 w-40"
            aria-label="Delete selected items"
          >
            <FiTrash className="mr-2" />
            <span className="flex-1 text-center">Delete</span>
          </button>
        </div>
      </div>

      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        placeholder="Search users..."
        style="px-4 w-1/3"
      />

      {/* {showContentModal && <MyModal onClose={() => setShowContentModal(false)} />} */}
      <TableUserList
        data={searchResults}
        columns={column}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        onRequestDelete={requestRowDeletion}
        onEdit={handleEditClick}
      />

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 w-full max-w-md mx-auto rounded shadow-lg overflow-hidden p-4">
            <p className="text-gray-800 dark:text-gray-200 text-center text-lg md:text-base">
              Are you sure you want to delete the selected items?
            </p>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="bg-gray-300 dark:bg-gray-600 dark:text-gray-200 px-4 py-2 rounded mr-2 text-sm md:text-base hover:bg-gray-400 dark:hover:bg-gray-700 transition duration-300"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  rowToDelete !== null ? confirmDelete() : deleteSelectedRows()
                }
                className="bg-red-500 text-white px-4 py-2 rounded text-sm md:text-base hover:bg-red-600 transition duration-300"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {showContentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-md flex justify-center items-center p-4">
          <div className="bg-white dark:bg-gray-800 w-full max-w-lg mx-auto rounded shadow-lg overflow-hidden p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {modalMode === 'edit' ? 'Edit User' : 'Add New User'}
            </h2>
            <form onSubmit={handleAddNewSubmit}>
              {/* Username Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Username
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.username}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      username: e.target.value,
                    })
                  }
                  required
                  minLength={3}
                />
              </div>

              {/* Name Input */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <input
                  type="text"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.name}
                  onChange={(e) =>
                    setEditableData({ ...editableData, name: e.target.value })
                  }
                  required
                />
              </div>

              {/* Email Input - Editable only in 'add' mode */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Email
                </label>
                <input
                  type="email"
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.email}
                  onChange={(e) =>
                    setEditableData({ ...editableData, email: e.target.value })
                  }
                  readOnly={modalMode === 'edit'}
                  required
                />
              </div>

              {/* Password Fields - Only in 'add' mode */}
              {modalMode === 'add' && (
                <>
                  <div className="mb-4 relative">
                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        type={tempShowPassword ? 'text' : 'password'}
                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-2.5 pr-10 h-10 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                        value={editableData.password}
                        onChange={(e) =>
                          setEditableData({
                            ...editableData,
                            password: e.target.value,
                          })
                        }
                        required
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center justify-center px-3 h-full text-sm text-gray-600 dark:text-gray-400"
                        onMouseDown={() => setTempShowPassword(true)}
                        onMouseUp={() => setTempShowPassword(false)}
                        onMouseLeave={() => setTempShowPassword(false)}
                      >
                        {tempShowPassword ? (
                          <FiEyeOff size={20} />
                        ) : (
                          <FiEye size={20} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                      value={editableData.confirmPassword}
                      onChange={(e) =>
                        setEditableData({
                          ...editableData,
                          confirmPassword: e.target.value,
                        })
                      }
                      required
                    />
                    {editableData.password !== editableData.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">
                        Passwords do not match.
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Gender Dropdown */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  Gender
                </label>
                <select
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.gender}
                  onChange={
                    (e) =>
                      setEditableData({
                        ...editableData,
                        gender: e.target.value.toLowerCase(),
                      }) // Normalize the value to lower case
                  }
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="prefer_not_to_say">Prefer not to say</option>
                </select>
              </div>

              {/* UserRole Dropdown */}
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  User Role
                </label>
                <select
                  className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  value={editableData.userRole}
                  onChange={(e) =>
                    setEditableData({
                      ...editableData,
                      userRole: e.target.value,
                    })
                  }
                  required
                >
                  <option value="">Select Role</option>
                  <option value="superadmin">Super Admin</option>
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                </select>
              </div>

              <div className="flex justify-end mt-4">
                <button
                  type="button"
                  onClick={() => setShowContentModal(false)}
                  className="bg-gray-300 dark:bg-gray-600 dark:text-gray-200 px-4 py-2 rounded mr-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
